import { inject, injectable } from 'inversify';
import type { IPointerHandler } from '@/components/viewPort/PointerController';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { IObjectViewModel, IViewPortManager } from '../../interfaces';
import type { IIndicationsController, IIndicationStrategy } from './Indications.controller';
import { IndicationsController } from './Indications.controller';
import type { IndicationsService } from './Indications.service';
import { IndicationsPointerHandler } from './IndicationsPointerHandler';
import { TYPES } from './iocTypes';
import type { Tools } from './ToolStateStore';
import type { IIndicationsViewModelFactory } from './interfaces';
import type { ICursorSetter } from './ToolsManager';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ICommandProcessor } from '../core/interfaces/Command';
import type { IRemover } from '@/engine/UIPresentation/Interfaces';

export interface IIndicationToolFactory {
  createIndicationStrategy: (
    planningAlgorithm: PlanningAlgorithm,
    commandProcessor: ICommandProcessor,
    remover: IRemover
  ) => IIndicationStrategy;
  createIndicationModel: () => IValue;
  createIndicationViewModel: (vmFactory: IIndicationsViewModelFactory, phaseId: string | undefined) => IObjectViewModel;
}

export interface IIndicationsFactory {
  createIndicationsController: (tool: Tools) => IIndicationsController;
  createIndicationsPointerHandler: (
    vpm: IViewPortManager,
    ctrl: IIndicationsController
  ) => IPointerHandler & ICursorSetter;
  getIndicationService: () => IndicationsService;
}

@injectable()
export class IndicationsFactory implements IIndicationsFactory {
  private _indicationsService: IndicationsService;

  public constructor(@inject(TYPES.IndicationsService) indicationsService: IndicationsService) {
    this._indicationsService = indicationsService;
  }

  public createIndicationsController(tool: Tools): IndicationsController {
    const strategy = this._indicationsService.getIndicationStrategyForTool(tool);

    return new IndicationsController(strategy);
  }

  public getIndicationService(): IndicationsService {
    return this._indicationsService;
  }

  public createIndicationsPointerHandler(
    vpm: IViewPortManager,
    indicationsController: IIndicationsController
  ): IndicationsPointerHandler {
    return new IndicationsPointerHandler(vpm, indicationsController);
  }
}
